import React, { useState } from "react";
import { Box, Card, CardContent, Grid, Stack, styled, Typography } from "@mui/material";
import Tooltip, { TooltipProps, tooltipClasses } from '@mui/material/Tooltip';
import HeatMapRiskList from "./HeatMapRiskList";
import RiskPanelHeader from "./RiskPanelHeader";

const HtmlTooltip = styled(({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
        background: 'none',
        color: 'rgba(0, 0, 0, 0.87)',
        // maxWidth: 220,
        // fontSize: theme.typography.pxToRem(12),
        // border: '1px solid #dadde9',
    },
}));

const colors = [
    ["#FED653", "#FE970A", "#EE4F3E", "#EE4F3E", "#EE4F3E"],
    ["#68C485", "#FED653", "#FE970A", "#EE4F3E", "#EE4F3E"],
    ["#68C485", "#FED653", "#FED653", "#FE970A", "#EE4F3E"],
    ["#68C485", "#68C485", "#FED653", "#FED653", "#FE970A"],
    ["#68C485", "#68C485", "#68C485", "#68C485", "#68C485"],
];

const CELL_HEIGHT = 56;

const riskLegend = [
    { label: "Very Low", color: "#6CC48A" },
    { label: "Medium", color: "#FFD54F" },
    { label: "High", color: "#FFA000" },
    { label: "Very High", color: "#EF5350" },
];

const xAxisValues = [1, 2, 3, 4, 5];
const yAxisValues = [5, 4, 3, 2, 1];        // Likelihood (top → bottom)

const HeatMap = ({ data }) => {

    console.log(data?.data?.cells?.find((value) => value?.likelihood == 2 && value?.impact == 3));


    const [isRiskList, setIsRiskList] = useState(false);
    const [rowData, setRowData] = useState(null);

    const heatMapData = Array.from({ length: data?.data?.maxLikelihood }, () =>
        Array(data?.data?.maxImpact).fill(0)
    );

    data?.data?.cells?.forEach(({ likelihood, impact, count }) => {
        // Likelihood: bottom -> top
        const yIndex = data.data.maxLikelihood - likelihood;

        // Impact: left -> right
        const xIndex = impact - 1;

        heatMapData[yIndex][xIndex] = count;
    });

    const handleGetRisk = (likelihood, impact) => {
        setIsRiskList((prev) => {
            const newState = !prev;
            setRowData(newState ? { likelihood, impact } : null);
            return newState;
        });
    }

    const handleClose = () => {
        setIsRiskList(false)
        setRowData(null)
    }

    return (
        <Card
            elevation={0}
            sx={{
                borderRadius: 3,
                border: "1px solid #E4E4E4",
                width: '100%',
                height: { xs: "auto", md: 450 },
                boxSizing: "border-box",
            }}
        >
            <RiskPanelHeader eyebrow="Severity matrix" title="Likelihood and Impact" subtitle="Locate exposure hotspots by combining risk probability with delivery impact." />
            <Box sx={{
                width: '100%',
                borderBottom: "1px solid #E4E4E4",
                p: 1.5,
                display: 'none',
                justifyContent: 'space-between'
            }}>
                <Box>
                    <Typography variant="subtitle1" sx={{
                        fontFamily: "Outfit, sans-serif",
                        fontWeight: 600,
                        fontSize: '20px',
                        letterSpacing: "0px",
                        mb: 0
                    }}>
                        Heat Map
                    </Typography>
                    <Typography variant="body2" sx={{
                        fontFamily: "Outfit, sans-serif",
                        fontWeight: 300,
                        fontSize: '16px',
                        letterSpacing: "0px",
                        color: '#7D7F81'
                    }}>
                        Visual grid showing risk likelihood versus impact to spotlight critical hotspots.
                    </Typography>
                </Box>
            </Box>
            <Grid container>
                <Grid size={{ md: 12 }} sx={{ p: 1.5, pl: 5, position: 'relative' }}>

                    <Typography
                        sx={{
                            position: "absolute",
                            left: "0px",
                            top: "40%",
                            transform: "rotate(-90deg) translateY(-50%)",
                            fontSize: "13px",
                            fontWeight: 600,
                            color: "#182A4E",
                            fontFamily: "Outfit, sans-serif",
                        }}
                    >
                        Likelihood
                    </Typography>
                    {/* X Axis Label */}
                    <Typography
                        sx={{
                            textAlign: "center",
                            mb: 1,
                            fontSize: "13px",
                            fontWeight: 600,
                            color: "#182A4E",
                            fontFamily: "Outfit, sans-serif",
                        }}
                    >
                        Impact
                    </Typography>

                    <Box sx={{ display: "flex" }}>
                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateRows: `repeat(5, ${CELL_HEIGHT}px)`,
                                justifyContent: "center",
                                alignItems: "center",
                                mr: 1,
                            }}
                        >
                            {yAxisValues.map((val, index) => (
                                <Typography
                                    key={index}
                                    sx={{
                                        fontSize: "12px",
                                        fontWeight: 500,
                                        color: "#182A4E",
                                        fontFamily: "Outfit, sans-serif",
                                        textAlign: "center",
                                    }}
                                >
                                    {val}
                                </Typography>
                            ))}
                        </Box>
                        <Box sx={{ width: '100%' }}>
                            <Box
                                sx={{
                                    display: "grid",
                                    gridTemplateColumns: "repeat(5, 1fr)",
                                    gridAutoRows: `${CELL_HEIGHT}px`,
                                    width: "100%",
                                    maxWidth: 900,
                                    borderRadius: "3px",
                                    overflow: "hidden",
                                }}
                            >
                                {heatMapData.map((row, rowIndex) =>
                                    row.map((value, colIndex) => (

                                        <HtmlTooltip
                                            title={
                                                <React.Fragment>
                                                    {value > 0 &&
                                                        <Card elevation={3} sx={{ minWidth: 180 }}>
                                                            <CardContent sx={{ padding: "12px !important" }}>
                                                                <Box display="flex" justifyContent="space-between" >
                                                                    <Typography variant="body2" fontFamily={"Outfit, sans-serif"}>Likelihood</Typography>
                                                                    <Typography variant="body2" fontWeight={600} fontFamily={"Outfit, sans-serif"}>
                                                                        {heatMapData?.length - rowIndex}
                                                                    </Typography>
                                                                </Box>

                                                                <Box display="flex" justifyContent="space-between">
                                                                    <Typography variant="body2" fontFamily={"Outfit, sans-serif"}>Impact</Typography>
                                                                    <Typography variant="body2" fontFamily={"Outfit, sans-serif"} fontWeight={600}>
                                                                        {colIndex + 1}
                                                                    </Typography>
                                                                </Box>

                                                                <Box display="flex" justifyContent="space-between">
                                                                    <Typography variant="body2" fontFamily={"Outfit, sans-serif"}>Count</Typography>
                                                                    <Typography variant="body2" fontFamily={"Outfit, sans-serif"} fontWeight={600}>
                                                                        {value}
                                                                    </Typography>
                                                                </Box>
                                                            </CardContent>
                                                        </Card>
                                                    }

                                                </React.Fragment>
                                            }
                                        >
                                            <Box
                                                key={`${rowIndex}-${colIndex}`}
                                                id={`${rowIndex + 1}-${colIndex + 1}`}
                                                onClick={() => {
                                                    if (value > 0) {
                                                        handleGetRisk(heatMapData?.length - rowIndex, colIndex + 1)
                                                    }
                                                }
                                                }
                                                sx={{
                                                    backgroundColor: colors[rowIndex][colIndex],
                                                    display: "flex",
                                                    flexDirection: "column",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    border: "2px solid #fff",
                                                    cursor: value ? "pointer" : '',
                                                    height: CELL_HEIGHT,
                                                    minHeight: CELL_HEIGHT,
                                                    boxSizing: "border-box",
                                                    px: 0.5,
                                                    py: 0.25,
                                                    overflow: "hidden",
                                                }}
                                            >
                                                {value > 0 &&
                                                    <>
                                                        <Typography
                                                            sx={{
                                                                color: "#fff",
                                                                fontWeight: 500,
                                                                fontSize: "12px",
                                                                fontFamily: "Outfit, sans-serif",
                                                                lineHeight: "14px",
                                                            }}
                                                        >
                                                            {value}
                                                        </Typography>
                                                        <Typography
                                                            sx={{
                                                                color: "#fff",
                                                                fontSize: "10px",
                                                                fontFamily: "Outfit, sans-serif",
                                                                lineHeight: "12px",
                                                                whiteSpace: "nowrap",
                                                            }}
                                                        >
                                                            Mitigated: {data?.data?.cells?.find((value) => value?.likelihood == heatMapData?.length - rowIndex && value?.impact == colIndex + 1)?.mitigatedCount ?? 0}
                                                        </Typography>

                                                        <Typography
                                                            sx={{
                                                                color: "#fff",
                                                                fontSize: "10px",
                                                                fontFamily: "Outfit, sans-serif",
                                                                lineHeight: "12px",
                                                                whiteSpace: "nowrap",
                                                            }}
                                                        >
                                                            Not Mitigated: {data?.data?.cells?.find((value) => value?.likelihood == heatMapData?.length - rowIndex && value?.impact == colIndex + 1)?.nonMitigatedCount ?? 0}
                                                        </Typography>
                                                    </>
                                                }

                                            </Box>
                                        </HtmlTooltip>
                                    ))
                                )}
                            </Box>
                            <Box
                                sx={{
                                    display: "grid",
                                    gridTemplateColumns: `repeat(${5}, 1fr)`,
                                    mt: 0.5,
                                }}
                            >
                                {xAxisValues.map((val) => (
                                    <Typography
                                        sx={{
                                            fontSize: "12px",
                                            fontWeight: 500,
                                            color: "#182A4E",
                                            fontFamily: "Outfit, sans-serif",
                                            textAlign: "center",
                                        }}
                                    >
                                        {val}
                                    </Typography>
                                ))}
                            </Box>
                        </Box>

                    </Box>
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "48px",
                            backgroundColor: "#FFFFFF",
                            borderRadius: "8px",
                            mt: 3,
                        }}
                    >
                        {riskLegend.map((item, index) => (
                            <Box
                                key={index}
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "5px",
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 12,
                                        height: 12,
                                        backgroundColor: item.color,
                                        borderRadius: "2px",
                                    }}
                                />
                                <Typography
                                    sx={{
                                        fontSize: "13px",
                                        fontWeight: 500,
                                        color: "#182A4E",
                                        fontFamily: "Outfit, sans-serif",
                                    }}
                                >
                                    {item.label}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                </Grid>
            </Grid>
            {isRiskList &&
                <HeatMapRiskList open={isRiskList} data={rowData} handleClose={handleClose} />
            }
        </Card>

    );
};

export default HeatMap;
